<template>
  <v-container fluid class="pa-4 inscritos-ciclo">
    <Alerta v-if="respuestaAlerta" :parametros="parametros" />

    <!-- Encabezado + filtros -->
    <v-card class="shadowCard mb-4" elevation="0">
      <v-card-text class="encabezado__contenido">
        <div class="d-flex align-center encabezado__titulo">
          <v-avatar color="primary" size="40" rounded class="mr-3">
            <v-icon dark>mdi-account-group</v-icon>
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Inscritos por ciclo</div>
            <div class="text-caption grey--text">
              <template v-if="resumen">
                {{ nombreEscuela }} · {{ nombreCiclo }}
                <span v-if="resumen.ciclo_anterior"> · vs {{ resumen.ciclo_anterior.ciclo }}</span>
              </template>
              <template v-else>Conteo de alumnos inscritos por sucursal</template>
            </div>
          </div>
        </div>

        <div class="encabezado__filtros">
          <v-btn-toggle
            v-model="escuela"
            mandatory
            color="primary"
            class="toggle-escuela"
          >
            <v-btn :value="1" small text>INBI</v-btn>
            <v-btn :value="2" small text>FAST</v-btn>
          </v-btn-toggle>

          <v-autocomplete
            v-model="idCiclo"
            :items="ciclos"
            item-text="ciclo"
            item-value="id_ciclo"
            placeholder="Ciclo"
            outlined
            dense
            hide-details
            :loading="cargandoCiclos"
            prepend-inner-icon="mdi-calendar-range"
            class="encabezado__ciclo"
          />

          <v-btn
            small
            depressed
            color="primary"
            :disabled="!idCiclo"
            :loading="consultando"
            @click="consultar"
          >
            <v-icon left small>mdi-magnify</v-icon>
            Consultar
          </v-btn>
          <v-btn
            small
            outlined
            color="green darken-1"
            :disabled="!resumen"
            @click="exportarResumen"
          >
            <v-icon left small>mdi-microsoft-excel</v-icon>
            Excel
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Cargando -->
    <template v-if="consultando">
      <v-row dense class="mb-2">
        <v-col v-for="n in 6" :key="'sk-' + n" cols="6" sm="4" lg="2">
          <v-skeleton-loader type="card-heading, text" class="shadowCard" />
        </v-col>
      </v-row>
      <v-skeleton-loader type="table-tbody" class="shadowCard" />
    </template>

    <!-- Estado vacío -->
    <v-card v-else-if="!resumen" class="shadowCard" elevation="0">
      <v-card-text class="text-center py-12">
        <v-icon size="64" color="grey lighten-1">mdi-chart-box-outline</v-icon>
        <div class="text-subtitle-1 mt-3">Selecciona escuela y ciclo</div>
        <div class="text-body-2 grey--text">Pulsa «Consultar» para ver el conteo de inscritos por sucursal.</div>
      </v-card-text>
    </v-card>

    <template v-else>
      <!-- Indicadores -->
      <v-row dense class="mb-2">
        <v-col v-for="kpi in indicadores" :key="kpi.clave" cols="6" sm="4" lg="2">
          <v-card
            class="shadowCard kpi-card"
            elevation="0"
            :class="{ 'kpi-card--click': kpi.detalle }"
            @click="kpi.detalle && abrirDetalle(resumen.totales, kpi.clave)"
          >
            <v-card-text class="d-flex align-center pa-3">
              <v-avatar :color="kpi.color" size="42" class="mr-3">
                <v-icon dark>{{ kpi.icono }}</v-icon>
              </v-avatar>
              <div class="kpi-card__texto">
                <div class="text-caption grey--text text--darken-1">{{ kpi.titulo }}</div>
                <div class="text-h5 font-weight-bold">{{ formatoNumero(kpi.valor) }}</div>
                <div class="text-caption" :class="kpi.claseNota">{{ kpi.nota }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Distribución por modalidad -->
      <v-card class="shadowCard mb-4" elevation="0">
        <v-card-text class="py-3">
          <div class="d-flex align-center flex-wrap mb-2">
            <span class="text-subtitle-2 mr-4">Distribución por modalidad</span>
            <span v-for="m in modalidades" :key="'l-' + m.clave" class="leyenda mr-4">
              <span class="leyenda__punto" :style="{ background: m.color }" />
              {{ m.titulo }}: <strong class="ml-1">{{ formatoNumero(m.valor) }}</strong>
              <span class="grey--text ml-1">({{ m.pct }}%)</span>
            </span>
          </div>
          <div class="barra-modalidad">
            <div
              v-for="m in modalidades"
              v-show="m.valor > 0"
              :key="'b-' + m.clave"
              class="barra-modalidad__segmento"
              :style="{ width: m.pct + '%', background: m.color }"
              :title="m.titulo + ': ' + m.valor"
              @click="abrirDetalle(resumen.totales, m.clave)"
            />
          </div>
        </v-card-text>
      </v-card>

      <!-- Tabla / Gráfica -->
      <v-card class="shadowCard" elevation="0">
        <v-tabs v-model="tab" color="primary" class="px-2">
          <v-tab><v-icon left small>mdi-table</v-icon>Por sucursal</v-tab>
          <v-tab><v-icon left small>mdi-chart-bar</v-icon>Gráfica</v-tab>
        </v-tabs>
        <v-divider />

        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-card-text class="pb-2">
              <v-row dense align="center">
                <v-col cols="12" sm="5" md="4">
                  <v-text-field
                    v-model="buscarSucursal"
                    placeholder="Buscar sucursal"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col cols="12" sm="4" md="3">
                  <v-select
                    v-model="orden"
                    :items="opcionesOrden"
                    prepend-inner-icon="mdi-sort"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-spacer />
                <v-col cols="12" sm="auto" class="text-caption grey--text">
                  <v-icon x-small>mdi-cursor-default-click</v-icon>
                  Haz clic en un número para ver los alumnos
                </v-col>
              </v-row>
            </v-card-text>

            <div class="tabla-wrap">
              <table class="tabla">
                <thead>
                  <tr class="tabla__grupos">
                    <th rowspan="2" class="tabla__sucursal">Sucursal</th>
                    <th colspan="3">Modalidad</th>
                    <th colspan="2">Tipo</th>
                    <th rowspan="2">Becados</th>
                    <th rowspan="2" class="tabla__col-total">Total</th>
                    <th rowspan="2">Bajas</th>
                    <th colspan="2">vs ciclo anterior</th>
                  </tr>
                  <tr class="tabla__columnas">
                    <th>Adultos</th>
                    <th>TEENS</th>
                    <th>Online</th>
                    <th>NI</th>
                    <th>RI</th>
                    <th>Anterior</th>
                    <th>Variación</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="fila in filasVisibles" :key="'f-' + fila.id_plantel">
                    <td class="tabla__sucursal">{{ fila.plantel }}</td>
                    <td v-for="col in columnasNumero" :key="col" :class="{ 'tabla__col-total': col === 'total' }">
                      <template v-if="col === 'total'">
                        <a v-if="fila.total > 0" class="numero numero--total" @click="abrirDetalle(fila, 'total')">
                          {{ formatoNumero(fila.total) }}
                        </a>
                        <span v-else class="numero--cero">–</span>
                        <v-progress-linear
                          :value="participacion(fila)"
                          height="3"
                          rounded
                          color="primary"
                          background-color="grey lighten-3"
                          class="mt-1"
                        />
                      </template>
                      <template v-else>
                        <a
                          v-if="fila[col] > 0"
                          class="numero"
                          :class="'numero--' + col"
                          @click="abrirDetalle(fila, col)"
                        >{{ formatoNumero(fila[col]) }}</a>
                        <span v-else class="numero--cero">–</span>
                      </template>
                    </td>
                    <td class="grey--text text--darken-1">{{ formatoNumero(fila.total_anterior) }}</td>
                    <td>
                      <v-chip
                        x-small
                        label
                        :color="colorVariacion(fila.variacion)"
                        text-color="white"
                        class="font-weight-bold"
                      >
                        <v-icon x-small left>{{ iconoVariacion(fila.variacion) }}</v-icon>
                        {{ formatoVariacion(fila) }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr v-if="!filasVisibles.length">
                    <td colspan="11" class="text-center grey--text py-6">Sin sucursales con ese filtro</td>
                  </tr>
                </tbody>

                <tfoot>
                  <tr>
                    <td class="tabla__sucursal">TOTAL</td>
                    <td v-for="col in columnasNumero" :key="'t-' + col" :class="{ 'tabla__col-total': col === 'total' }">
                      <a
                        v-if="resumen.totales[col] > 0"
                        class="numero"
                        @click="abrirDetalle(resumen.totales, col)"
                      >{{ formatoNumero(resumen.totales[col]) }}</a>
                      <span v-else>–</span>
                    </td>
                    <td>{{ formatoNumero(resumen.totales.total_anterior) }}</td>
                    <td>
                      <v-chip
                        x-small
                        label
                        :color="colorVariacion(resumen.totales.variacion)"
                        text-color="white"
                        class="font-weight-bold"
                      >
                        <v-icon x-small left>{{ iconoVariacion(resumen.totales.variacion) }}</v-icon>
                        {{ formatoVariacion(resumen.totales) }}
                      </v-chip>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </v-tab-item>

          <v-tab-item>
            <v-card-text>
              <div class="text-caption grey--text mb-2">
                Nuevos ingresos (NI) y reinscritos (RI) activos por sucursal, ordenado por total.
              </div>
              <apexchart
                v-if="tab === 1"
                type="bar"
                :height="alturaGrafica"
                :options="opcionesGrafica"
                :series="seriesGrafica"
              />
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </template>

    <!-- Detalle de alumnos -->
    <v-dialog v-model="dialogDetalle" max-width="1150px" scrollable>
      <v-card>
        <v-toolbar color="primary" dark flat dense>
          <v-icon left>mdi-account-details</v-icon>
          <v-toolbar-title class="text-subtitle-1">
            {{ detalleInfo.titulo }}
            <span class="text-caption ml-2 detalle__sub">{{ detalleInfo.sucursal }} · {{ nombreCiclo }}</span>
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            small
            depressed
            color="green darken-1"
            class="mr-2"
            :disabled="!detalle.length"
            @click="exportarDetalle"
          >
            <v-icon left small>mdi-microsoft-excel</v-icon>
            Excel
          </v-btn>
          <v-btn icon @click="dialogDetalle = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <div class="d-flex flex-wrap align-center mb-3">
            <v-chip small class="mr-2 mb-1" color="primary" outlined>
              <v-icon small left>mdi-account-multiple</v-icon>
              {{ detalle.length }} alumnos
            </v-chip>
            <v-chip v-if="resumenDetalle.ni" small class="mr-2 mb-1" color="teal" outlined>NI: {{ resumenDetalle.ni }}</v-chip>
            <v-chip v-if="resumenDetalle.ri" small class="mr-2 mb-1" color="indigo" outlined>RI: {{ resumenDetalle.ri }}</v-chip>
            <v-chip v-if="resumenDetalle.becados" small class="mr-2 mb-1" color="amber darken-3" outlined>
              Becados: {{ resumenDetalle.becados }}
            </v-chip>
            <v-chip v-if="resumenDetalle.bajas" small class="mr-2 mb-1" color="red" outlined>Bajas: {{ resumenDetalle.bajas }}</v-chip>
            <v-spacer />
            <v-text-field
              v-model="buscarDetalle"
              placeholder="Buscar matrícula, alumno o grupo"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              class="detalle__buscar"
            />
          </div>

          <v-data-table
            :headers="headersDetalle"
            :items="detalle"
            :search="buscarDetalle"
            :loading="cargandoDetalle"
            loading-text="Cargando alumnos..."
            item-key="id_alumno"
            dense
            class="tabla-detalle"
            :items-per-page="25"
            :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          >
            <template v-slot:item.matricula="{ item }">
              <span class="matricula">{{ item.matricula }}</span>
            </template>
            <template v-slot:item.alumno="{ item }">
              <div class="font-weight-medium">{{ item.alumno }}</div>
              <div class="text-caption grey--text">{{ item.plantel }}</div>
            </template>
            <template v-slot:item.grupo="{ item }">
              <span class="text-caption">{{ item.grupo }}</span>
            </template>
            <template v-slot:item.pagado="{ item }">{{ formatoMoneda(item.pagado) }}</template>
            <template v-slot:item.adeudo="{ item }">
              <span :class="Number(item.adeudo) > 0 ? 'red--text' : 'grey--text'">{{ formatoMoneda(item.adeudo) }}</span>
            </template>
            <template v-slot:item.beca="{ item }">
              <v-chip v-if="item.beca != null" x-small color="amber darken-2" dark>{{ item.beca }}%</v-chip>
              <span v-else class="grey--text">–</span>
            </template>
            <template v-slot:item.tipo="{ item }">
              <v-chip x-small label :color="item.tipo === 'NI' ? 'teal' : 'indigo'" dark>{{ item.tipo }}</v-chip>
            </template>
            <template v-slot:item.activo="{ item }">
              <v-chip x-small label :color="Number(item.activo) === 1 ? 'green' : 'red'" dark>
                {{ Number(item.activo) === 1 ? 'Activo' : 'Baja' }}
              </v-chip>
            </template>
            <template v-slot:item.fecha_alta="{ item }">{{ formatoFecha(item.fecha_alta) }}</template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Alerta from '@/components/alertas/Alerta.vue'
import validarErrores from '@/mixins/validarErrores'
import funcionesExcel from '@/mixins/funcionesExcel'

const ETIQUETAS_DETALLE = {
  adultos: 'Adultos',
  teens: 'TEENS',
  online: 'Online',
  ni: 'Nuevos ingresos (NI)',
  ri: 'Reinscritos (RI)',
  becados: 'Becados',
  total: 'Total inscritos',
  bajas: 'Bajas',
}

const COLORES = {
  adultos: '#42A5F5',
  teens: '#AB47BC',
  online: '#26A69A',
  ni: '#009688',
  ri: '#3F51B5',
}

export default {
  name: 'InscritosPorCiclo',
  components: { Alerta },
  mixins: [validarErrores, funcionesExcel],

  data () {
    return {
      escuela: 1,
      ciclos: [],
      idCiclo: null,
      cargandoCiclos: false,
      consultando: false,
      resumen: null,
      tab: 0,

      buscarSucursal: '',
      orden: 'plantel',
      opcionesOrden: [
        { text: 'Sucursal (A-Z)', value: 'plantel' },
        { text: 'Mayor total', value: 'total' },
        { text: 'Mayor crecimiento', value: 'variacion_desc' },
        { text: 'Mayor caída', value: 'variacion_asc' },
      ],
      columnasNumero: ['adultos', 'teens', 'online', 'ni', 'ri', 'becados', 'total', 'bajas'],

      dialogDetalle: false,
      cargandoDetalle: false,
      detalleInfo: { titulo: '', sucursal: '' },
      detalle: [],
      buscarDetalle: '',
      headersDetalle: [
        { text: 'Matrícula', value: 'matricula' },
        { text: 'Alumno', value: 'alumno' },
        { text: 'Grupo', value: 'grupo' },
        { text: 'Pagado', value: 'pagado', align: 'end' },
        { text: 'Adeudo', value: 'adeudo', align: 'end' },
        { text: 'Beca', value: 'beca', align: 'center' },
        { text: 'Tipo', value: 'tipo', align: 'center' },
        { text: 'Estatus', value: 'activo', align: 'center' },
        { text: 'Fecha alta', value: 'fecha_alta' },
      ],
    }
  },

  computed: {
    filas () {
      return (this.resumen && this.resumen.filas) || []
    },

    filasVisibles () {
      const texto = String(this.buscarSucursal || '').trim().toUpperCase()
      const filas = texto
        ? this.filas.filter((f) => String(f.plantel).toUpperCase().includes(texto))
        : [...this.filas]
      const comparadores = {
        plantel: (a, b) => String(a.plantel).localeCompare(String(b.plantel), 'es'),
        total: (a, b) => b.total - a.total,
        variacion_desc: (a, b) => b.variacion - a.variacion,
        variacion_asc: (a, b) => a.variacion - b.variacion,
      }
      return filas.sort(comparadores[this.orden] || comparadores.plantel)
    },

    nombreEscuela () {
      return this.escuela === 2 ? 'FAST' : 'INBI'
    },

    nombreCiclo () {
      return this.resumen && this.resumen.ciclo ? this.resumen.ciclo.ciclo : ''
    },

    indicadores () {
      if (!this.resumen) return []
      const t = this.resumen.totales
      const anterior = this.resumen.ciclo_anterior
      return [
        {
          clave: 'total',
          titulo: 'Total inscritos',
          valor: t.total,
          icono: 'mdi-account-group',
          color: 'primary',
          detalle: true,
          nota: anterior ? `${this.formatoVariacion(t)} vs anterior` : 'Sin ciclo anterior',
          claseNota: this.claseVariacion(t.variacion),
        },
        {
          clave: 'ni',
          titulo: 'Nuevos ingresos (NI)',
          valor: t.ni,
          icono: 'mdi-account-plus',
          color: 'teal',
          detalle: true,
          nota: `${this.porcentaje(t.ni, t.total)}% del total`,
          claseNota: 'grey--text',
        },
        {
          clave: 'ri',
          titulo: 'Reinscritos (RI)',
          valor: t.ri,
          icono: 'mdi-account-convert',
          color: 'indigo',
          detalle: true,
          nota: `${this.porcentaje(t.ri, t.total)}% del total`,
          claseNota: 'grey--text',
        },
        {
          clave: 'becados',
          titulo: 'Becados',
          valor: t.becados,
          icono: 'mdi-school',
          color: 'amber darken-2',
          detalle: true,
          nota: `${this.porcentaje(t.becados, t.total)}% del total`,
          claseNota: 'grey--text',
        },
        {
          clave: 'bajas',
          titulo: 'Bajas',
          valor: t.bajas,
          icono: 'mdi-account-remove',
          color: 'red lighten-1',
          detalle: true,
          nota: 'No suman al total',
          claseNota: 'grey--text',
        },
        {
          clave: 'anterior',
          titulo: 'Ciclo anterior',
          valor: t.total_anterior,
          icono: 'mdi-history',
          color: 'blue-grey',
          detalle: false,
          nota: anterior ? anterior.ciclo : '—',
          claseNota: 'grey--text',
        },
      ]
    },

    modalidades () {
      if (!this.resumen) return []
      const t = this.resumen.totales
      return [
        { clave: 'adultos', titulo: 'Adultos', valor: t.adultos },
        { clave: 'teens', titulo: 'TEENS', valor: t.teens },
        { clave: 'online', titulo: 'Online', valor: t.online },
      ].map((m) => ({ ...m, color: COLORES[m.clave], pct: this.porcentaje(m.valor, t.total) }))
    },

    resumenDetalle () {
      const r = { ni: 0, ri: 0, becados: 0, bajas: 0 }
      for (const a of this.detalle) {
        if (Number(a.activo) !== 1) {
          r.bajas += 1
          continue
        }
        r[a.tipo === 'NI' ? 'ni' : 'ri'] += 1
        if (a.beca != null) r.becados += 1
      }
      return r
    },

    filasGrafica () {
      return [...this.filas].sort((a, b) => b.total - a.total)
    },

    alturaGrafica () {
      return Math.max(320, this.filasGrafica.length * 28 + 80)
    },

    seriesGrafica () {
      return [
        { name: 'NI', data: this.filasGrafica.map((f) => f.ni) },
        { name: 'RI', data: this.filasGrafica.map((f) => f.ri) },
      ]
    },

    opcionesGrafica () {
      return {
        chart: {
          stacked: true,
          toolbar: { show: true },
          fontFamily: 'inherit',
          events: {
            dataPointSelection: (event, ctx, cfg) => {
              const fila = this.filasGrafica[cfg.dataPointIndex]
              if (fila) this.abrirDetalle(fila, cfg.seriesIndex === 0 ? 'ni' : 'ri')
            },
          },
        },
        colors: [COLORES.ni, COLORES.ri],
        plotOptions: {
          bar: { horizontal: true, barHeight: '70%', borderRadius: 3 },
        },
        dataLabels: {
          enabled: true,
          style: { fontSize: '10px' },
          formatter: (val) => (val > 0 ? val : ''),
        },
        xaxis: { categories: this.filasGrafica.map((f) => f.plantel) },
        yaxis: { labels: { style: { fontSize: '11px' }, maxWidth: 180 } },
        grid: { borderColor: '#eeeeee' },
        legend: { position: 'top', horizontalAlign: 'left' },
        tooltip: { shared: true, intersect: false },
      }
    },
  },

  watch: {
    escuela () {
      this.resumen = null
      this.cargarCiclos()
    },
  },

  created () {
    this.cargarCiclos()
  },

  methods: {
    async cargarCiclos () {
      this.cargandoCiclos = true
      this.idCiclo = null
      this.ciclos = []
      try {
        const response = await this.$http.get('kpi.inscritos.ciclo.ciclos', {
          params: { escuela: this.escuela },
        })
        this.ciclos = response.body || response.data || []
        const hoy = new Date().toISOString().slice(0, 10)
        const actual = this.ciclos.find((c) => String(c.fecha_inicio_ciclo || '').slice(0, 10) <= hoy)
        this.idCiclo = actual ? actual.id_ciclo : (this.ciclos[0] && this.ciclos[0].id_ciclo) || null
      } catch (error) {
        this.validarError(error)
      } finally {
        this.cargandoCiclos = false
      }
    },

    async consultar () {
      if (!this.idCiclo) return
      this.consultando = true
      this.resumen = null
      try {
        const response = await this.$http.get('kpi.inscritos.ciclo.resumen', {
          params: { escuela: this.escuela, id_ciclo: this.idCiclo },
        })
        this.resumen = response.body || response.data || null
      } catch (error) {
        this.validarError(error)
      } finally {
        this.consultando = false
      }
    },

    async abrirDetalle (fila, tipo) {
      this.detalleInfo = {
        titulo: ETIQUETAS_DETALLE[tipo],
        sucursal: fila.id_plantel == null ? 'Todas las sucursales' : fila.plantel,
      }
      this.detalle = []
      this.buscarDetalle = ''
      this.dialogDetalle = true
      this.cargandoDetalle = true
      try {
        const params = { escuela: this.escuela, id_ciclo: this.resumen.ciclo.id_ciclo, tipo }
        if (fila.id_plantel != null) params.id_plantel = fila.id_plantel
        const response = await this.$http.get('kpi.inscritos.ciclo.detalle', { params })
        const data = response.body || response.data || {}
        this.detalle = data.alumnos || []
      } catch (error) {
        this.dialogDetalle = false
        this.validarError(error)
      } finally {
        this.cargandoDetalle = false
      }
    },

    porcentaje (valor, total) {
      if (!total) return 0
      return Math.round((valor / total) * 1000) / 10
    },

    participacion (fila) {
      const total = this.resumen ? this.resumen.totales.total : 0
      return total ? (fila.total / total) * 100 : 0
    },

    formatoNumero (valor) {
      return Number(valor || 0).toLocaleString('es-MX')
    },

    formatoVariacion (fila) {
      if (!this.resumen || !this.resumen.ciclo_anterior) return '—'
      const signo = fila.variacion > 0 ? '+' : ''
      const pct = fila.variacion_pct == null ? '' : ` (${signo}${fila.variacion_pct}%)`
      return `${signo}${fila.variacion}${pct}`
    },

    claseVariacion (valor) {
      if (valor > 0) return 'green--text text--darken-2 font-weight-bold'
      if (valor < 0) return 'red--text font-weight-bold'
      return 'grey--text'
    },

    colorVariacion (valor) {
      if (valor > 0) return 'green darken-1'
      if (valor < 0) return 'red darken-1'
      return 'grey'
    },

    iconoVariacion (valor) {
      if (valor > 0) return 'mdi-arrow-up'
      if (valor < 0) return 'mdi-arrow-down'
      return 'mdi-minus'
    },

    formatoMoneda (valor) {
      if (valor == null) return '—'
      return Number(valor).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
    },

    formatoFecha (valor) {
      if (!valor) return '—'
      const [y, m, d] = String(valor).slice(0, 10).split('-')
      return d && m && y ? `${d}/${m}/${y}` : String(valor)
    },

    exportarResumen () {
      if (!this.resumen) return
      const filas = [...this.filas, this.resumen.totales].map((f) => ({
        Sucursal: f.plantel,
        Adultos: f.adultos,
        TEENS: f.teens,
        Online: f.online,
        NI: f.ni,
        RI: f.ri,
        Becados: f.becados,
        Total: f.total,
        Bajas: f.bajas,
        'Ciclo anterior': f.total_anterior,
        Variación: f.variacion,
        'Variación %': f.variacion_pct,
      }))
      this.exportarExcelDirecto(filas, `INSCRITOS_${this.nombreEscuela}_${this.nombreCiclo}`.replace(/\s+/g, '_'))
    },

    exportarDetalle () {
      const filas = this.detalle.map((a) => ({
        Matrícula: a.matricula,
        Alumno: a.alumno,
        Sucursal: a.plantel,
        Grupo: a.grupo,
        Pagado: a.pagado,
        Adeudo: a.adeudo,
        'Beca %': a.beca,
        Tipo: a.tipo,
        Estatus: Number(a.activo) === 1 ? 'ACTIVO' : 'BAJA',
        'Fecha alta': this.formatoFecha(a.fecha_alta),
      }))
      this.exportarExcelDirecto(filas, `DETALLE_${this.nombreEscuela}_${this.nombreCiclo}`.replace(/\s+/g, '_'))
    },
  },
}
</script>

<style scoped>
.encabezado__contenido {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px !important;
}
.encabezado__filtros {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.encabezado__ciclo {
  width: 230px;
  flex: 0 0 230px;
}
.toggle-escuela {
  border: 1px solid #e0e0e0;
  border-radius: 6px !important;
  overflow: hidden;
}
.toggle-escuela .v-btn {
  height: 38px !important;
  min-width: 64px !important;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.kpi-card {
  height: 100%;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.kpi-card--click {
  cursor: pointer;
}
.kpi-card--click:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08) !important;
}
.kpi-card__texto {
  min-width: 0;
  line-height: 1.2;
}

.leyenda {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
}
.leyenda__punto {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
}
.barra-modalidad {
  display: flex;
  height: 14px;
  border-radius: 7px;
  overflow: hidden;
  background: #eeeeee;
}
.barra-modalidad__segmento {
  height: 100%;
  cursor: pointer;
  transition: opacity 0.15s ease;
}
.barra-modalidad__segmento:hover {
  opacity: 0.8;
}

.tabla-wrap {
  overflow: auto;
  max-height: calc(100vh - 260px);
  border-top: 1px solid #eeeeee;
}
.tabla {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.tabla th,
.tabla td {
  padding: 6px 10px;
  text-align: center;
  white-space: nowrap;
  border-bottom: 1px solid #f0f0f0;
}
.tabla thead th {
  position: sticky;
  z-index: 2;
  background: #fafafa;
  color: #616161;
  font-weight: 600;
  font-size: 12px;
}
.tabla__grupos th {
  top: 0;
  height: 32px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 11px !important;
  border-bottom: 1px solid #e0e0e0;
}
.tabla__columnas th {
  top: 32px;
  border-bottom: 2px solid #e0e0e0;
}
.tabla__grupos th[rowspan] {
  text-transform: none;
  letter-spacing: normal;
  font-size: 12px !important;
  border-bottom: 2px solid #e0e0e0;
}
.tabla .tabla__sucursal {
  text-align: left;
  font-weight: 600;
  position: sticky;
  left: 0;
  z-index: 1;
  background: #ffffff;
  min-width: 170px;
}
.tabla thead .tabla__sucursal {
  z-index: 3;
  background: #fafafa;
}
.tabla tbody tr:hover td {
  background: #f5f9ff;
}
.tabla__col-total {
  background: #f7f9fc;
  min-width: 90px;
}
.tabla tfoot td {
  position: sticky;
  bottom: 0;
  z-index: 1;
  background: #eceff1;
  font-weight: 700;
  border-top: 2px solid #cfd8dc;
}
.tabla tfoot .tabla__sucursal {
  z-index: 2;
  background: #eceff1;
}

.numero {
  display: inline-block;
  min-width: 34px;
  padding: 1px 6px;
  border-radius: 10px;
  color: inherit !important;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease;
}
.numero:hover {
  background: rgba(0, 0, 0, 0.08);
}
.numero--total {
  font-weight: 700;
  font-size: 14px;
}
.numero--ni { color: #00796b !important; }
.numero--ri { color: #303f9f !important; }
.numero--becados { color: #ef6c00 !important; }
.numero--bajas { color: #e53935 !important; }
.numero--cero {
  color: #bdbdbd;
}

.detalle__sub {
  opacity: 0.85;
}
.detalle__buscar {
  max-width: 340px;
}
.matricula {
  font-family: monospace;
  font-size: 12px;
}
.tabla-detalle ::v-deep td {
  vertical-align: middle;
}
</style>
